import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';

// Check if we're in test mode (no Firebase credentials or force test mode)
let isTestMode = !process.env.REACT_APP_FIREBASE_API_KEY ||
                   process.env.REACT_APP_FIREBASE_API_KEY === 'test-key' ||
                   process.env.REACT_APP_USE_TEST_MODE === 'true';

let app = null;
let auth = null;
let database = null;

// Only initialize Firebase if not in test mode
if (!isTestMode) {
  try {
    const { initializeApp } = require('firebase/app');
    /* eslint-disable no-unused-vars */
    const {
      getAuth, signInAnonymously, onAuthStateChanged, signOut, updateProfile
    } = require('firebase/auth');
    const { getDatabase, ref, set, get } = require('firebase/database');
    /* eslint-enable no-unused-vars */

    // Firebase configuration
    const firebaseConfig = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
    };

    // Initialize Firebase
    console.log('🔥 Initializing Firebase with config:', firebaseConfig);
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    database = getDatabase(app);
    
    // Enable persistence
    const { setPersistence, browserLocalPersistence } = require('firebase/auth');
    setPersistence(auth, browserLocalPersistence)
      .then(() => console.log('✅ Firebase persistence enabled'))
      .catch((error) => console.error('❌ Failed to enable persistence:', error));
    
    console.log('✅ Firebase initialized successfully');
  } catch (error) {
    console.error('❌ Firebase initialization failed:', error);
    console.log('Firebase initialization failed, using test mode');
    isTestMode = true;
  }
}

const AuthContext = createContext();

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authInProgress, setAuthInProgress] = useState(false);
  const authListenerInitialized = useRef(false);
  const lastUserUid = useRef(null);

  // Sign in with Google (host)
  const signInWithGoogle = async () => {
    if (isTestMode) {
      toast.error('Google sign-in not available in test mode');
      throw new Error('Not available in test mode');
    }
    try {
      setAuthInProgress(true);
      const { GoogleAuthProvider, signInWithPopup } = require('firebase/auth');
      const provider = new GoogleAuthProvider();
      const result   = await signInWithPopup(auth, provider);
      // user comes with displayName / email filled
      return result.user;
    } finally {
      setAuthInProgress(false);
    }
  };

  // Email / password sign-up (host)
  const signUpWithEmail = async (email, password, displayName) => {
    if (isTestMode) {
      toast.error('Email sign-up not available in test mode');
      throw new Error('Not available in test mode');
    }
    try {
      setAuthInProgress(true);
      const { createUserWithEmailAndPassword, updateProfile } = require('firebase/auth');
      const { ref, set } = require('firebase/database');
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
        await updateProfile(cred.user, { displayName });
      }
      // create DB record
      const db = require('firebase/database').getDatabase();
      await set(ref(db, `users/${cred.user.uid}`), {
        uid: cred.user.uid,
        displayName: cred.user.displayName || displayName || email,
        email: cred.user.email,
        isGuest: false,
        createdAt: new Date().toISOString(),
        lastSeen: new Date().toISOString()
      });
      toast.success('Account created');
      return cred.user;
    } finally {
      setAuthInProgress(false);
    }
  };

  // Email / password sign-in (host)
  const signInWithEmail = async (email, password) => {
    if (isTestMode) {
      toast.error('Email sign-in not available in test mode');
      throw new Error('Not available in test mode');
    }
    try {
      setAuthInProgress(true);
      const { signInWithEmailAndPassword } = require('firebase/auth');
      const cred = await signInWithEmailAndPassword(auth, email, password);
      return cred.user;
    } finally {
      setAuthInProgress(false);
    }
  };

  // Sign in as guest
  const signInAsGuest = async (displayName) => {
    if (authInProgress) {
      console.log('🔄 Auth already in progress, skipping...');
      return null;
    }

    try {
      setAuthInProgress(true);
      setLoading(true);
      const timestamp = () => new Date().toISOString();
      
      if (isTestMode) {
        // Test mode: create a mock user
        const userId = `test-${Date.now()}`;
        const mockUser = {
          uid: userId,
          displayName: displayName,
          isGuest: true,
          createdAt: new Date().toISOString(),
          lastSeen: new Date().toISOString(),
          getIdToken: async () => {
            // Create a simple JWT-like token for testing
            const payload = {
              uid: userId,
              displayName: displayName,
              isGuest: true,
              iat: Math.floor(Date.now() / 1000),
              exp: Math.floor(Date.now() / 1000) + 3600 // 1 hour
            };
            return btoa(JSON.stringify(payload)); // Simple base64 encoding
          }
        };
        
        setUser(mockUser);
        lastUserUid.current = userId;
        // Save user to localStorage for axios interceptor
        localStorage.setItem('auxbox-user', JSON.stringify(mockUser));
        toast.success('Welcome to JukeHive! (Test Mode)');
        return mockUser;
      } else {
        // Real Firebase mode
        console.log(`[${timestamp()}] 🔐 Starting Firebase sign in for: ${displayName}`);
        const { signInAnonymously, updateProfile } = require('firebase/auth');
        const { ref, set } = require('firebase/database');
        
        const result = await signInAnonymously(auth);
        console.log(`[${timestamp()}] ✅ Firebase sign in successful: ${result.user.uid}`);
        
        // Update profile with display name
        await updateProfile(result.user, {
          displayName: displayName
        });
        console.log(`[${timestamp()}] ✅ Profile updated with display name: ${displayName}`);
        // Force token refresh so new displayName is included
        await result.user.getIdToken(true);

        // Create user record in database
        await set(ref(database, `users/${result.user.uid}`), {
          uid: result.user.uid,
          displayName: displayName,
          isGuest: true,
          createdAt: new Date().toISOString(),
          lastSeen: new Date().toISOString()
        });
        console.log(`[${timestamp()}] ✅ User record created in database`);
        
        // Create user object with proper token method binding
        const userWithToken = {
          uid: result.user.uid,
          displayName: displayName,
          isGuest: true,
          email: result.user.email,
          photoURL: result.user.photoURL,
          getIdToken: async () => {
            try {
              return await result.user.getIdToken();
            } catch (error) {
              console.error('Error getting ID token:', error);
              return null;
            }
          }
        };
        
        console.log(`[${timestamp()}] 🔥 Setting user state: ${userWithToken.displayName}`);
        setUser(userWithToken);
        lastUserUid.current = result.user.uid;
        
        // Save to localStorage for persistence
        localStorage.setItem('auxbox-user', JSON.stringify({
          uid: userWithToken.uid,
          displayName: userWithToken.displayName,
          isGuest: userWithToken.isGuest
        }));
        
        toast.success('Welcome to JukeHive!');
        return userWithToken;
      }
    } catch (error) {
      console.error('Guest sign in error:', error);
      toast.error('Failed to sign in as guest');
      throw error;
    } finally {
      setLoading(false);
      setAuthInProgress(false);
    }
  };

  // Rename an already-signed-in guest. Firebase persists anonymous users
  // across visits, so a returning device keeps its old displayName unless
  // we explicitly overwrite it and refresh the token (the server reads the
  // name from the token's claims).
  const updateGuestName = async (newName) => {
    const name = (newName || '').trim();
    if (!name) return;

    if (isTestMode) {
      setUser((prev) => (prev ? { ...prev, displayName: name } : prev));
      const stored = JSON.parse(localStorage.getItem('auxbox-user') || 'null');
      if (stored) {
        localStorage.setItem('auxbox-user', JSON.stringify({ ...stored, displayName: name }));
      }
      sessionStorage.setItem('guestName', name);
      return;
    }

    const current = auth.currentUser;
    if (!current) return;

    const { updateProfile } = require('firebase/auth');
    const { ref, update } = require('firebase/database');

    await updateProfile(current, { displayName: name });
    // Force a token refresh so the new name claim reaches the server
    await current.getIdToken(true);

    try {
      await update(ref(database, `users/${current.uid}`), {
        displayName: name,
        lastSeen: new Date().toISOString()
      });
    } catch (e) {
      console.warn('Could not update user record with new name:', e);
    }

    setUser((prev) => (prev ? { ...prev, displayName: name } : prev));
    const stored = JSON.parse(localStorage.getItem('auxbox-user') || 'null');
    if (stored) {
      localStorage.setItem('auxbox-user', JSON.stringify({ ...stored, displayName: name }));
    }
    // Keep the stored entry name in sync so a later visit doesn't "rename"
    // the user back to a stale value
    sessionStorage.setItem('guestName', name);
  };

  // Sign out
  const logout = async () => {
    try {
      if (isTestMode) {
        setUser(null);
        lastUserUid.current = null;
        localStorage.removeItem('auxbox-user');
        toast.success('Signed out successfully (Test Mode)');
      } else {
        const { signOut } = require('firebase/auth');
        await signOut(auth);
        lastUserUid.current = null;
        toast.success('Signed out successfully');
      }
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Failed to sign out');
    }
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    try {
      if (isTestMode) {
        setUser(prev => ({ ...prev, ...updates }));
        toast.success('Profile updated successfully (Test Mode)');
      } else {
        const { updateProfile } = require('firebase/auth');
        const { ref, update } = require('firebase/database');

        await updateProfile(user, updates);

        // Update in database (merge, don't overwrite)
        await update(ref(database, `users/${user.uid}`), {
          ...updates,
          lastSeen: new Date().toISOString()
        });

        toast.success('Profile updated successfully');
      }
    } catch (error) {
      console.error('Update profile error:', error);
      toast.error('Failed to update profile');
    }
  };

  // Get user data from database
  const getUserData = async (uid) => {
    try {
      if (isTestMode) {
        return null; // No database in test mode
      } else {
        const { ref, get } = require('firebase/database');
        const snapshot = await get(ref(database, `users/${uid}`));
        return snapshot.val();
      }
    } catch (error) {
      console.error('Get user data error:', error);
      return null;
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const timestamp = () => new Date().toISOString();
    console.log(`[${timestamp()}] 🎯 Setting up auth listener, test mode: ${isTestMode}`);
    
    if (authListenerInitialized.current) {
      console.log(`[${timestamp()}] ⚠️ Auth listener already initialized, skipping`);
      return;
    }
    
    if (isTestMode) {
      // Test mode: restore user from localStorage
      try {
        const savedUser = localStorage.getItem('auxbox-user');
        if (savedUser) {
          const userData = JSON.parse(savedUser);
          // Recreate the user object with proper getIdToken method
          const mockUser = {
            ...userData,
            getIdToken: async () => {
              const payload = {
                uid: userData.uid,
                displayName: userData.displayName,
                isGuest: userData.isGuest,
                iat: Math.floor(Date.now() / 1000),
                exp: Math.floor(Date.now() / 1000) + 3600
              };
              return btoa(JSON.stringify(payload));
            }
          };
          setUser(mockUser);
          lastUserUid.current = userData.uid;
          console.log(`[${timestamp()}] ✅ User restored from localStorage: ${userData.displayName}`);
        }
      } catch (error) {
        console.error('Failed to restore user from localStorage:', error);
        localStorage.removeItem('auxbox-user');
      }
      setLoading(false);
      authListenerInitialized.current = true;
      return;
    }
    
    // Firebase mode
    if (!auth) {
      console.log(`[${timestamp()}] ⚠️ Auth not initialized yet`);
      setLoading(false);
      authListenerInitialized.current = true;
      return;
    }
    
    const { onAuthStateChanged } = require('firebase/auth');
    console.log(`[${timestamp()}] 🔍 Setting up Firebase auth listener...`);
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log(`[${timestamp()}] 🔥 Firebase auth state changed: ${firebaseUser ? `User ${firebaseUser.uid}` : 'No user'}`);
      
      // Prevent duplicate state updates
      if (firebaseUser && lastUserUid.current === firebaseUser.uid) {
        console.log(`[${timestamp()}] ⚠️ Same user already set, skipping update`);
        setLoading(false);
        return;
      }
      
      if (firebaseUser) {
        // Get additional user data from database
        const userData = await getUserData(firebaseUser.uid);
        console.log(`[${timestamp()}] 📦 User data from database:`, userData);
        
        // Create a user object that properly binds the getIdToken method
        const userWithToken = {
          uid: firebaseUser.uid,
          displayName: firebaseUser.displayName || userData?.displayName,
          isGuest: firebaseUser.isAnonymous,
          email: firebaseUser.email,
          photoURL: firebaseUser.photoURL,
          getIdToken: async () => {
            try {
              return await firebaseUser.getIdToken();
            } catch (error) {
              console.error('Error getting ID token:', error);
              return null;
            }
          }
        };
        
        setUser(userWithToken);
        lastUserUid.current = firebaseUser.uid;
        console.log(`[${timestamp()}] ✅ User set in state from auth listener: ${userWithToken.displayName}, ${userWithToken.uid}`);
        
        // Also save to localStorage for persistence
        localStorage.setItem('auxbox-user', JSON.stringify({
          uid: userWithToken.uid,
          displayName: userWithToken.displayName,
          isGuest: userWithToken.isGuest
        }));
      } else {
        console.log(`[${timestamp()}] ❌ No Firebase user found, setting user to null`);
        setUser(null);
        lastUserUid.current = null;
        localStorage.removeItem('auxbox-user');
      }
      setLoading(false);
    });

    authListenerInitialized.current = true;
    return unsubscribe;
  }, []);

  const value = {
    user,
    loading,
    authInProgress,
    // Guest
    signInAsGuest,
    updateGuestName,
    // Host options
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    logout,
    updateUserProfile,
    getUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 